<template>
  <div v-if="editing">
    <div
      class="
        tw-bg-sf-neutral-subtle-2
        tw-rounded-lg
        tw-p-2
        tw-flex
        tw-flex-row
        tw-gap-x-6
        tw-gap-y-3
      "
      :class="{ 'tw-flex-wrap': wrap }"
    >
      <span
        class="remark-tags-multiselect"
        :class="{ 'tw-w-full': wrap, 'tw-min-w-[300px]': !wrap }"
      >
        <SfMultiselect
          v-model="editableTagIds"
          data-testid="remark-tags"
          mode="tags"
          :can-clear="true"
          :clear-on-select="true"
          :hide-selected="true"
          :options="tagOptions"
          :placeholder="I18n.t('communication.remark_form.tags_placeholder')"
          :is-client-meeting-remark="isClientMeetingRemark"
          :wide-dropdown="false"
        >
          <template #noresults="{ query }">
            <span v-if="query.length === 0"></span>
          </template>
        </SfMultiselect>
      </span>
      <span
        class="tw-flex tw-flex-row tw-gap-4 tw-items-center tw-justify-end"
        :class="{ 'tw-grow': wrap }"
      >
        <a @click="stopEditing">
          {{ I18n.t('common.form.cancel') }}
        </a>
        <SfButtonDs
          :disabled="saveDisabled"
          size="sm"
          variant="primary"
          @click="saveChanges"
        >
          {{ I18n.t('common.save') }}
        </SfButtonDs>
      </span>
    </div>
  </div>
  <div v-else-if="tagIds.length">
    <RemarkTagsList
      :tag-ids="tagIds.filter((id) => !excludeTagIdsInCard.includes(id))"
      :disable-links="props.disableLinks"
    />
  </div>
</template>

<script setup>
import RemarkTagsList from '@app/application/communication/sections/communication/components/remarks/remark-tags-list.vue'
import useI18n from '@app/shared/vue/composables/use-I18n'
import { computed, ref, watch } from 'vue'
import { useStore } from 'vuex'
import isEqual from 'lodash/isEqual'
import SfMultiselect from '@app/shared/vue/components/sf-multiselect.vue'
import SfButtonDs from '@app/shared/vue/components/sf-button/sf-button-ds.vue'
import orderBy from 'lodash/orderBy'

const I18n = useI18n()
const store = useStore()

const tagOptions = computed(() => {
  const allTags = store.state.remarkTags?.allRemarkTags || {}
  const result = []
  for (const tag of orderBy(
    Object.values(allTags),
    ['talkingPoint', 'name'],
    ['desc', 'asc']
  )) {
    if (!tag.archived || editableTagIds.value.includes(tag.id)) {
      result.push({
        value: tag.id,
        label: tag.name,
        archived: tag.archived,
        icon: tag.talkingPoint ? 'fa-solid fa-user-group' : false,
      })
    }
  }
  return result
})

const props = defineProps({
  editing: {
    type: Boolean,
  },
  wrap: {
    type: Boolean,
  },
  tagIds: {
    type: Array,
    default: () => [],
  },
  remarkId: {
    type: Number,
    required: true,
  },
  isClientMeetingRemark: {
    type: Boolean,
    default: false,
  },
  disableLinks: {
    type: Boolean,
    default: false,
  },
  excludeTagIdsInCard: {
    type: Array,
    default: () => [],
  },
})

const emit = defineEmits(['stopEditing'])

const editableTagIds = ref([...props.tagIds])
watch(
  () => props.tagIds,
  () => {
    editableTagIds.value = [...props.tagIds]
  }
)

const saveDisabled = computed(() => isEqual(props.tagIds, editableTagIds.value))

function stopEditing() {
  emit('stopEditing')
}

async function saveChanges() {
  await store.dispatch('updateRemark', [
    props.remarkId,
    {
      tagIds: editableTagIds.value,
    },
  ])
  stopEditing()
}
</script>

<style scoped>
.remark-tags-multiselect :deep(.multiselect) {
  min-width: 0;
}
</style>
